/* eslint-disable no-unused-vars */
import React from "react";
import {
  generateUID as defaultGenerateUID,
  isSameDate as defaultIsSameDate,
  getSafeTime as defaultGetSafeTime
} from "../lib/helpers";

// Default helpers if not passed via props
const defaultGetCycleDisplayName = (cycleName) => {
  if (!cycleName) return "";
  if (cycleName.includes(":")) {
    const parts = cycleName.split(":");
    return parts.slice(1).join(":");
  }
  return cycleName;
};

const defaultGetAvailableCycles = (selectedMachine, allCycles = []) => {
  if (!selectedMachine) {
    return (allCycles || []).filter(c => !c.includes(":"));
  }
  const machineSpecific = (allCycles || []).filter(c => c.startsWith(`${selectedMachine}:`));
  if (machineSpecific.length === 0) {
    return (allCycles || []).filter(c => !c.includes(":"));
  }
  return machineSpecific;
};

const defaultParseTimeFromCycle = (cycleText) => {
  if (!cycleText) return null;
  const match = cycleText.match(/(\d{1,2})[:.](\d{2})/);
  if (match) {
    return {
      hours: parseInt(match[1], 10),
      minutes: parseInt(match[2], 10)
    };
  }
  return null;
};

const defaultGetAutoSelectedCycle = (selectedMachine, allCycles = []) => {
  const available = defaultGetAvailableCycles(selectedMachine, allCycles);
  if (available.length === 0) return "";
  
  const now = new Date();
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTotal = currentHours * 60 + currentMinutes;
  
  let bestCycle = "";
  let minDiff = Infinity;
  
  for (const cycle of available) {
    const time = defaultParseTimeFromCycle(cycle);
    if (!time) continue;
    
    const cycleTotal = time.hours * 60 + time.minutes;
    let diff = cycleTotal - currentTotal;
    if (diff < 0) {
      diff += 24 * 60; // Next day
    }
    if (diff < minDiff) {
      minDiff = diff;
      bestCycle = cycle;
    }
  }
  return bestCycle || available[0] || "";
};

const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: "", zoom: "", other: "", document: "" };
  if (typeof imageUrlField !== "string") return { full: "", zoom: "", other: "", document: "" };

  if (imageUrlField.startsWith("{") && imageUrlField.endsWith("}")) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || "",
        zoom: parsed.zoom || "",
        other: parsed.other || "",
        document: parsed.document || ""
      };
    } catch (e) {
      return { full: imageUrlField, zoom: "", other: "", document: "" };
    }
  }
  return { full: imageUrlField, zoom: "", other: "", document: "" };
};

const PackagingPage = ({
  pkgType = "cloth",
  setPkgType,
  numBlankLabels = "",
  setNumBlankLabels,
  handlePrepareBlankLabels,
  isPrinting = false,
  isSterileInfoComplete: isSterileInfoCompleteProp,
  isAdmin = false,
  setActiveManagerField,
  preparedBy = "",
  setPreparedBy,
  getFilteredPreparers: getFilteredPreparersProp,
  checkedBy = "",
  setCheckedBy,
  getFilteredCheckers: getFilteredCheckersProp,
  sterileMachine = "",
  setSterileMachine,
  handleMachineChange: handleMachineChangeProp,
  machinesList = [],
  sterileCycle = "",
  setSterileCycle,
  getAvailableCycles = defaultGetAvailableCycles,
  cyclesList = [],
  getCycleDisplayName = defaultGetCycleDisplayName,
  getAutoSelectedCycle = defaultGetAutoSelectedCycle,
  instruments = [],
  selectedInspectionItem = null,
  setSelectedInspectionItem,
  isUsingPreparedScan = false,
  setIsUsingPreparedScan,
  isBarcodeScanned = false,
  setIsBarcodeScanned,
  setCameraTargetField,
  setIsCameraOpen,
  userProfiles = [],
  user = null,
  generateUID = defaultGenerateUID,
  handleConfirmInspection,
  targetBoxForPack = "",
  setTargetBoxForPack,
  itemMaster = [],
  supplies = [],
  handleCreateUniqueCode,
  scanBlankItem = null,
  setScanBlankItem,
  historyFilterDate: historyFilterDateProp,
  setHistoryFilterDate: setHistoryFilterDateProp,
  pkgHistoryDate,
  setPkgHistoryDate,
  selectedPrints = [],
  setSelectedPrints,
  handleBatchPrint,
  setPrintPreviewItem,
  handleCancelPackaging,

  // Extra props from specification
  sterileOptions = [],
  mfgDate = "",
  setMfgDate,
  expDate = "",
  setExpDate,
  packagingItem = null,
  setPackagingItem,
  logs = [],
  barcodeInputRef,
  handleBlurToFocus,
  handlePrintBarcode,
  printMode = "",
  setPrintMode,
  blankLabelsToPrint = [],
  setBlankLabelsToPrint,
  handleScanBlank,
  labelPreviewData = null,
  setShowLabelPreview,
  setShowAddOptionModal,
  setNewOptionCategory,
  setNewOptionValue,
  setIsSavingOption,
  setShowManageOptionsModal,
  setSelectedManageCategory,
  isSameDate = defaultIsSameDate,
  getSafeTime = defaultGetSafeTime,
  showPreview = {},
  setShowPreview,
  handleTogglePreview,
  parseImages = defaultParseImages,
}) => {
  const historyFilterDate = historyFilterDateProp !== undefined
    ? historyFilterDateProp
    : (pkgHistoryDate !== undefined ? pkgHistoryDate : "");

  const setHistoryFilterDate = setHistoryFilterDateProp || setPkgHistoryDate || (() => {});

  const isSterileInfoComplete = isSterileInfoCompleteProp !== undefined
    ? isSterileInfoCompleteProp
    : (Boolean(preparedBy && preparedBy.trim() !== "") &&
       Boolean(checkedBy && checkedBy.trim() !== "") &&
       Boolean(sterileMachine && sterileMachine.trim() !== "") &&
       sterileCycle !== "" && sterileCycle !== undefined && sterileCycle !== null);

  const handleMachineChange = handleMachineChangeProp || ((machineVal) => {
    if (setSterileMachine) setSterileMachine(machineVal);
    if (setSterileCycle) setSterileCycle(getAutoSelectedCycle(machineVal, cyclesList));
  });

  const getFilteredPreparers = getFilteredPreparersProp || (() => {
    const dummyNames = ["สมชาย", "สมหญิง", "สมศักดิ์", "สมศรี"];
    const userNames = (userProfiles || []).map(p => p.name).filter(Boolean);
    return Array.from(new Set(userNames)).filter(name => !dummyNames.includes(name));
  });

  const getFilteredCheckers = getFilteredCheckersProp || (() => {
    const dummyNames = ["สมชาย", "สมหญิง", "สมศักดิ์", "สมศรี"];
    const userNames = (userProfiles || []).map(p => p.name).filter(Boolean);
    return Array.from(new Set(userNames)).filter(name => !dummyNames.includes(name));
  });

  return (
          <div className="sterile-container">
            <div className="sterile-card">

              {/* Header */}
              <div className="sterile-header">
                <div style={{ fontSize: '3.5em', marginBottom: '10px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}>🏷️</div>
                <h2>ห้องตรวจสอบเครื่องมือก่อนฆ่าเชื้อ </h2>
                <p>พิมพ์ฉลาก บันทึกผู้เตรียม ผู้ตรวจ เครื่องและรอบนึ่ง เพื่อจัดเตรียมอุปกรณ์</p>
                <div className="accent-bar" style={{ background: 'linear-gradient(90deg, #00bfa5, #00796b, #1de9b6)' }} />
              </div>

              {/* PkgType Selector */}
              <div style={{ display: 'flex', gap: '15px', marginBottom: '25px' }}>
                <button
                  onClick={() => setPkgType('cloth')}
                  className={`sterile-mode-btn ${pkgType === 'cloth' ? 'active-cloth' : ''}`}
                  style={{ flex: 1, padding: '16px', fontSize: '1.1em' }}
                >
                  🧺 ห่อผ้า (7 วัน)
                </button>
                <button
                  onClick={() => setPkgType('sterile-pouch')}
                  className={`sterile-mode-btn ${pkgType === 'sterile-pouch' ? 'active-pouch' : ''}`}
                  style={{ flex: 1, padding: '16px', fontSize: '1.1em' }}
                >
                  📄 ซอง (6 เดือน)
                </button>
              </div>

              {/* Pre-print Blank Labels */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                padding: '20px',
                borderRadius: '16px',
                marginBottom: '25px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 5px 0', color: '#00e5ff' }}>🖨️ พิมพ์ฉลากเปล่าล่วงหน้า (Pre-print)</h4>
                  <p style={{ margin: 0, fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>เพื่อนำไปแปะซองรอไว้ แล้วค่อยนำมาสแกนผูกข้อมูลทีหลัง</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={numBlankLabels}
                    onChange={e => setNumBlankLabels(e.target.value)}
                    placeholder="จำนวนดวง (1-100)"
                    className="sterile-date-picker"
                    style={{ width: '130px', textAlign: 'center', fontSize: '1.05em', padding: '10px' }}
                  />
                  <button
                    onClick={handlePrepareBlankLabels}
                    disabled={isPrinting}
                    className="sterile-confirm-btn"
                    style={{ padding: '10px 20px', fontSize: '1em', width: 'auto', background: 'linear-gradient(135deg, #00e5ff 0%, #00b0ff 100%)', boxShadow: 'none' }}
                  >
                    พิมพ์ฉลากเปล่า
                  </button>
                </div>
              </div>

              {/* Required Details Alert */}
              {!isSterileInfoComplete && (
                <div style={{
                  background: 'rgba(231, 76, 60, 0.1)',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  color: '#ff7675',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  ⚠️ กรุณากรอกข้อมูลขั้นตอนการนึ่งนึ่งให้ครบถ้วน เพื่อปลดล็อกการจัดเตรียมและพิมพ์บาร์โค้ด
                </div>
              )}

              {/* 4 Required Inputs */}
              <div className="sterile-input-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>

                {/* 1. ผู้เตรียม */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>👤 ผู้เตรียม</label>
                    {isAdmin && (
                      <button
                        onClick={() => setActiveManagerField('preparer')}
                        style={{ background: 'none', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '0.82em', fontWeight: 'bold' }}
                      >
                        ⚙️ จัดการ
                      </button>
                    )}
                  </div>
                  <select
                    value={preparedBy}
                    onChange={(e) => setPreparedBy(e.target.value)}
                    className="sterile-input"
                    style={{ fontSize: '1.1em', padding: '12px', height: '52px', color: '#fff', background: 'rgba(255, 255, 255, 0.06)' }}
                  >
                    <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกผู้เตรียม --</option>
                    {(() => {
                      const list = getFilteredPreparers();
                      const final = list.includes(preparedBy) ? list : [preparedBy, ...list].filter(Boolean);
                      return final.map((name, idx) => (
                        <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{name}</option>
                      ));
                    })()}
                  </select>
                </div>

                {/* 2. ผู้ตรวจ */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>🔍 ผู้ตรวจ</label>
                  </div>
                  <select
                    value={checkedBy}
                    onChange={(e) => setCheckedBy(e.target.value)}
                    className="sterile-input"
                    style={{ fontSize: '1.1em', padding: '12px', height: '52px', color: '#fff', background: 'rgba(255, 255, 255, 0.03)', cursor: 'not-allowed', opacity: 0.8 }}
                    disabled={true}
                  >
                    <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกผู้ตรวจ --</option>
                    {(() => {
                      const list = getFilteredCheckers();
                      const final = list.includes(checkedBy) ? list : [checkedBy, ...list].filter(Boolean);
                      return final.map((name, idx) => (
                        <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{name}</option>
                      ));
                    })()}
                  </select>
                </div>

                {/* 3. เครื่องนึ่ง */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>⚙️ เครื่องนึ่ง</label>
                    {isAdmin && (
                      <button
                        onClick={() => setActiveManagerField('machine')}
                        style={{ background: 'none', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '0.82em', fontWeight: 'bold' }}
                      >
                        ⚙️ จัดการ
                      </button>
                    )}
                  </div>
                  <select
                    value={sterileMachine}
                    onChange={(e) => handleMachineChange(e.target.value)}
                    className="sterile-input"
                    style={{ fontSize: '1.1em', padding: '12px', height: '52px', color: '#fff', background: 'rgba(255, 255, 255, 0.06)' }}
                  >
                    <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกเครื่องนึ่ง --</option>
                    {machinesList.map((name, idx) => (
                      <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{name}</option>
                    ))}
                  </select>
                </div>

                {/* 4. รอบที่ */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>🔄 รอบที่</label>
                    {isAdmin && (
                      <button
                        onClick={() => setActiveManagerField('cycle')}
                        style={{ background: 'none', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '0.82em', fontWeight: 'bold' }}
                      >
                        ⚙️ จัดการ
                      </button>
                    )}
                  </div>
                  <select
                    value={sterileCycle}
                    onChange={(e) => setSterileCycle(e.target.value)}
                    className="sterile-input"
                    style={{ fontSize: '1.1em', padding: '12px', height: '52px', color: '#fff', background: 'rgba(255, 255, 255, 0.06)' }}
                  >
                    <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกรอบ --</option>
                    {getAvailableCycles(sterileMachine, cyclesList).map((name, idx) => (
                      <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{getCycleDisplayName(name)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pending Inspection from Tool Assembly Section */}
              {(() => {
                const pendingInspections = instruments.filter(i => i.status === 'assembled' && i.is_active);
                if (pendingInspections.length === 0) return null;
                return (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                    padding: '20px',
                    borderRadius: '16px',
                    marginBottom: '25px',
                    marginTop: '15px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#c084fc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📋 เครื่องมือจัดเสร็จ รอการตรวจสอบจากห้องจัดเครื่องมือ ({pendingInspections.length} รายการ)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                      {pendingInspections.map(item => (
                        <div 
                          key={item.id} 
                          style={{ 
                            background: 'rgba(168, 85, 247, 0.05)', 
                            border: '1px solid rgba(168, 85, 247, 0.2)', 
                            borderRadius: '12px', 
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '10px'
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <strong style={{ color: '#fff', fontSize: '1.05em', flex: 1 }}>{item.item_name}</strong>
                              <button
                                disabled={!isSterileInfoComplete}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedInspectionItem(item);
                                  setIsUsingPreparedScan(true);
                                  setIsBarcodeScanned(false);
                                  setCameraTargetField('direct-card-inspection');
                                  setIsCameraOpen(true);
                                  
                                  // Find assembler profile to get their name
                                  const assemblerEmailPrefix = item.details?.assembledBy || '';
                                  const assemblerProfile = userProfiles.find(p => p.email.toLowerCase().startsWith(assemblerEmailPrefix.toLowerCase()));
                                  const preparedName = assemblerProfile?.name || assemblerEmailPrefix || '';
                                  setPreparedBy(preparedName);

                                  // Prefill current user as checker ONLY IF checkedBy is not already set in the top panel
                                  if (!checkedBy) {
                                    const currentProfile = userProfiles.find(p => p.email.toLowerCase() === user?.email?.toLowerCase());
                                    const currentUserName = currentProfile?.name || (user?.email ? user.email.split('@')[0] : '');
                                    setCheckedBy(currentUserName);
                                  }

                                  // Prefill machine and cycle defaults if unset
                                  if (!sterileMachine) {
                                    setSterileMachine('หน่วยจ่ายกลาง');
                                    setSterileCycle(getAutoSelectedCycle('หน่วยจ่ายกลาง', cyclesList));
                                  } else if (!sterileCycle) {
                                    setSterileCycle(getAutoSelectedCycle(sterileMachine, cyclesList));
                                  }
                                }}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '10px',
                                  border: isSterileInfoComplete ? '2px solid #00e5ff' : '2px solid rgba(255,255,255,0.05)',
                                  background: isSterileInfoComplete ? 'linear-gradient(135deg, rgba(0, 229, 255, 0.25) 0%, rgba(0, 150, 255, 0.25) 100%)' : 'rgba(255,255,255,0.02)',
                                  color: isSterileInfoComplete ? '#00e5ff' : 'rgba(255,255,255,0.15)',
                                  cursor: isSterileInfoComplete ? 'pointer' : 'not-allowed',
                                  fontSize: '0.9em',
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap',
                                  minHeight: '44px',
                                  boxShadow: isSterileInfoComplete ? '0 0 10px rgba(0, 229, 255, 0.2)' : 'none'
                                }}
                                title={isSterileInfoComplete ? "กดเพื่อสแกน QR code" : "กรุณากรอกข้อมูลผู้ตรวจ, ผู้เตรียม, เครื่องนึ่ง, รอบการนึ่งให้ครบก่อน"}
                              >
                                📷 สแกน QR
                              </button>
                            </div>
                            <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                              {item.unique_barcode.startsWith('ASM-') ? (
                                <>
                                  รหัสชั่วคราว: <span style={{ fontFamily: 'monospace', color: '#ffb74d' }}>{item.unique_barcode}</span>
                                </>
                              ) : (
                                <>
                                  รหัสบาร์โค้ดจริง: <span style={{ fontFamily: 'monospace', color: '#00e5ff' }}>{item.unique_barcode}</span>
                                </>
                              )}
                            </div>
                            <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>
                              คนจัด: <span style={{ color: '#ffb74d', fontWeight: 'bold' }}>{item.details?.assembledBy || 'ไม่ระบุ'}</span>
                            </div>
                            <div style={{ fontSize: '0.82em', color: 'rgba(255,255,255,0.4)' }}>
                              วันที่จัด: {item.details?.assemblyDate || '-'}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedInspectionItem(item);
                              setIsUsingPreparedScan(true);
                              setIsBarcodeScanned(false);
                              
                              // Find assembler profile to get their name
                              const assemblerEmailPrefix = item.details?.assembledBy || '';
                              const assemblerProfile = userProfiles.find(p => p.email.toLowerCase().startsWith(assemblerEmailPrefix.toLowerCase()));
                              const preparedName = assemblerProfile?.name || assemblerEmailPrefix || '';
                              setPreparedBy(preparedName);

                              // Prefill current user as checker ONLY IF checkedBy is not already set in the top panel
                              if (!checkedBy) {
                                const currentProfile = userProfiles.find(p => p.email.toLowerCase() === user?.email?.toLowerCase());
                                const currentUserName = currentProfile?.name || (user?.email ? user.email.split('@')[0] : '');
                                setCheckedBy(currentUserName);
                              }

                              // Prefill machine and cycle defaults if unset
                              if (!sterileMachine) {
                                setSterileMachine('หน่วยจ่ายกลาง');
                                setSterileCycle(getAutoSelectedCycle('หน่วยจ่ายกลาง', cyclesList));
                              } else if (!sterileCycle) {
                                setSterileCycle(getAutoSelectedCycle(sterileMachine, cyclesList));
                              }
                            }}
                            className="sterile-confirm-btn"
                            style={{ 
                              padding: '8px 12px', 
                              fontSize: '0.85em', 
                              width: '100%', 
                              background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)', 
                              boxShadow: 'none',
                              marginTop: '5px'
                            }}
                          >
                            🔍 ตรวจสอบและพิมพ์บาร์โค้ด
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Active Inspection Modal Popup */}
              {selectedInspectionItem && (
                <div className="sterile-modal-overlay" onClick={() => setSelectedInspectionItem(null)} style={{ zIndex: 10005 }}>
                  <div className="sterile-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%', borderRadius: '20px', background: 'linear-gradient(135deg, #1e1b4b 0%, #311042 50%, #111827 100%)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '15px' }}>
                      <h3 style={{ margin: 0, fontSize: '1.3em', fontWeight: '700', color: '#c084fc' }}>
                        🔍 บันทึกผลการตรวจก่อนฆ่าเชื้อ
                      </h3>
                      <button
                        onClick={() => setSelectedInspectionItem(null)}
                        style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '1.2em', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ background: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{selectedInspectionItem.item_name}</div>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                          {selectedInspectionItem.unique_barcode.startsWith('ASM-') ? (
                            <>
                              รหัสชั่วคราว: <span style={{ fontFamily: 'monospace', color: '#ffb74d' }}>{selectedInspectionItem.unique_barcode}</span>
                            </>
                          ) : (
                            <>
                              รหัสบาร์โค้ดจริง: <span style={{ fontFamily: 'monospace', color: '#00e5ff' }}>{selectedInspectionItem.unique_barcode}</span>
                            </>
                          )}
                        </div>
                        <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)' }}>
                          ผู้จัดเครื่องมือ: <span style={{ color: '#ffb74d', fontWeight: 'bold' }}>{selectedInspectionItem.details?.assembledBy || 'ไม่ระบุ'}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsUsingPreparedScan(true);
                              setIsBarcodeScanned(false);
                              setCameraTargetField('inspection');
                              setIsCameraOpen(true);
                            }}
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid rgba(0, 229, 255, 0.4)',
                              background: 'rgba(0, 229, 255, 0.1)',
                              color: '#00e5ff',
                              fontSize: '0.85em',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 229, 255, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(0, 229, 255, 0.1)';
                            }}
                          >
                            📷 สแกน QR Code
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => {
                                setIsUsingPreparedScan(false);
                                setIsBarcodeScanned(false);
                                const newUid = generateUID(88);
                                setSelectedInspectionItem(prev => ({
                                  ...prev,
                                  unique_barcode: newUid
                                }));
                              }}
                              style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(192, 132, 252, 0.4)',
                                background: 'rgba(192, 132, 252, 0.1)',
                                color: '#c084fc',
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(192, 132, 252, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(192, 132, 252, 0.1)';
                              }}
                            >
                              ⚡ สร้าง QR Code ใหม่
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Dropdown: Packaging */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>📄 รูปแบบการห่อ (Packaging)</label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => setPkgType('cloth')}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '8px',
                              border: `1px solid ${pkgType === 'cloth' ? '#00e5ff' : 'rgba(255,255,255,0.1)'}`,
                              background: pkgType === 'cloth' ? 'rgba(0, 229, 255, 0.15)' : 'rgba(255,255,255,0.03)',
                              color: '#fff',
                              fontWeight: pkgType === 'cloth' ? 'bold' : 'normal',
                              cursor: 'pointer'
                            }}
                          >
                            🧺 ห่อผ้า (7 วัน)
                          </button>
                          <button
                            type="button"
                            onClick={() => setPkgType('sterile-pouch')}
                            style={{
                              flex: 1,
                              padding: '10px',
                              borderRadius: '8px',
                              border: `1px solid ${pkgType === 'sterile-pouch' ? '#00e5ff' : 'rgba(255,255,255,0.1)'}`,
                              background: pkgType === 'sterile-pouch' ? 'rgba(0, 229, 255, 0.15)' : 'rgba(255,255,255,0.03)',
                              color: '#fff',
                              fontWeight: pkgType === 'sterile-pouch' ? 'bold' : 'normal',
                              cursor: 'pointer'
                            }}
                          >
                            📄 ซอง (6 เดือน)
                          </button>
                        </div>
                      </div>

                      {/* Dropdown: ผู้เตรียม */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>👤 ผู้เตรียม</label>
                        <select
                          value={preparedBy}
                          onChange={(e) => setPreparedBy(e.target.value)}
                          className="sterile-input"
                          style={{ width: '100%', padding: '12px', color: '#fff', background: 'rgba(255, 255, 255, 0.06)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }}
                        >
                          <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกผู้เตรียม --</option>
                          {(() => {
                            const list = getFilteredPreparers();
                            const final = list.includes(preparedBy) ? list : [preparedBy, ...list].filter(Boolean);
                            return final.map((name, idx) => (
                              <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{name}</option>
                            ));
                          })()}
                        </select>
                      </div>

                      {/* Dropdown: ผู้ตรวจ */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>🔍 ผู้ตรวจ</label>
                        <select
                          value={checkedBy}
                          onChange={(e) => setCheckedBy(e.target.value)}
                          className="sterile-input"
                          style={{ width: '100%', padding: '12px', color: '#fff', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', cursor: 'not-allowed', opacity: 0.8 }}
                          disabled={true}
                        >
                          <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกผู้ตรวจ --</option>
                          {(() => {
                            const list = getFilteredCheckers();
                            const final = list.includes(checkedBy) ? list : [checkedBy, ...list].filter(Boolean);
                            return final.map((name, idx) => (
                              <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{name}</option>
                            ));
                          })()}
                        </select>
                      </div>

                      {/* Dropdown: เครื่องนึ่ง */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>⚙️ เครื่องนึ่ง</label>
                        <select
                          value={sterileMachine}
                          onChange={(e) => handleMachineChange(e.target.value)}
                          className="sterile-input"
                          style={{ width: '100%', padding: '12px', color: '#fff', background: 'rgba(255, 255, 255, 0.06)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }}
                        >
                          <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกเครื่องนึ่ง --</option>
                          {machinesList.map((name, idx) => (
                            <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Dropdown: รอบที่ */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.7)', fontWeight: 'bold' }}>🔄 รอบที่</label>
                        <select
                          value={sterileCycle}
                          onChange={(e) => setSterileCycle(e.target.value)}
                          className="sterile-input"
                          style={{ width: '100%', padding: '12px', color: '#fff', background: 'rgba(255, 255, 255, 0.06)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }}
                        >
                          <option value="" style={{ background: '#24243e', color: '#aaa' }}>-- เลือกรอบ --</option>
                          {getAvailableCycles(sterileMachine, cyclesList).map((name, idx) => (
                            <option key={idx} value={name} style={{ background: '#24243e', color: '#fff' }}>{getCycleDisplayName(name)}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '25px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '15px' }}>
                      <button
                        onClick={() => setSelectedInspectionItem(null)}
                        className="sterile-confirm-btn"
                        style={{ width: 'auto', padding: '10px 20px', fontSize: '0.9em', background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', boxShadow: 'none' }}
                      >
                        ยกเลิก
                      </button>
                      {isUsingPreparedScan && !isBarcodeScanned ? (
                        <button
                          onClick={() => {
                            setCameraTargetField('inspection');
                            setIsCameraOpen(true);
                          }}
                          className="sterile-confirm-btn"
                          style={{ width: 'auto', padding: '10px 24px', fontSize: '0.9em', background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)', boxShadow: 'none' }}
                        >
                          📷 สแกนบาร์โค้ด
                        </button>
                      ) : (
                        <button
                          onClick={handleConfirmInspection}
                          className="sterile-confirm-btn"
                          style={{ width: 'auto', padding: '10px 24px', fontSize: '0.9em', background: 'linear-gradient(135deg, #00e5ff 0%, #00b0ff 100%)', boxShadow: 'none' }}
                        >
                          ✅ บันทึกและพิมพ์
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Target items selector wrapper with lock overlay */}
              <div style={{ position: 'relative', marginTop: '20px' }}>
                {/* Blur overlay if not complete */}
                {!isSterileInfoComplete && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 12, 41, 0.65)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '20px',
                    zIndex: 100,
                    color: '#ffb74d',
                    border: '1px dashed rgba(255, 183, 77, 0.25)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
                  }}>
                    <span style={{ fontSize: '3em', marginBottom: '15px' }}>🔒</span>
                    <span style={{ fontSize: '1.5em', fontWeight: 'bold', letterSpacing: '0.5px' }}>กรุณากรอกข้อมูลให้ครบ</span>
                    <span style={{ fontSize: '0.9em', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>(ผู้เตรียม, ผู้ตรวจ, เครื่อง, รอบ)</span>
                  </div>
                )}

                {/* Blurrable content */}
                <div style={{
                  filter: !isSterileInfoComplete ? 'blur(3px)' : 'none',
                  pointerEvents: !isSterileInfoComplete ? 'none' : 'auto',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Target Box Selector */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    padding: '20px',
                    borderRadius: '16px',
                    marginBottom: '25px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#00e5ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🧲 ระบุกล่องเป้าหมาย <small style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 'normal' }}>(ให้บนซองมีชื่อกล่อง และสแกนคืนกล่องได้อัตโนมัติ)</small>
                    </h4>
                    <select
                      value={targetBoxForPack}
                      onChange={(e) => setTargetBoxForPack(e.target.value)}
                      className="sterile-date-picker"
                      style={{ width: '100%', padding: '12px', fontSize: '1.05em', borderRadius: '10px' }}
                    >
                      <option value="" style={{ background: '#24243e', color: '#fff' }}>-- ไม่ระบุ (จัดเป็นของเดี่ยวปกติ) --</option>
                      {instruments.filter(i => i.unique_barcode.startsWith('BOX-') && i.is_active).map(b => (
                        <option key={b.id} value={`${b.unique_barcode}|${b.item_name}`} style={{ background: '#24243e', color: '#fff' }}>
                          📦 {b.item_name} ({b.unique_barcode})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 1. ส่วนของ "ชุดตรวจ": จะถูกซ่อนถ้ามีการเลือกกล่องเป้าหมาย */}
                  {!targetBoxForPack && (
                    <div style={{ marginBottom: '25px' }}>
                      <h4 style={{ color: '#d1c4e9', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        📦 จัดเป็นชุดตรวจ (Sets)
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '15px' }}>
                        {itemMaster.filter(masterLot => masterLot.is_set && masterLot.category !== 'กล่องหัวกรอ').map(masterLot => {
                          const minAvailable = masterLot.components.reduce((min, compId) => {
                            const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
                            return Math.min(min, ws?.item_amount || 0);
                          }, 9999);

                          return (
                            <div key={masterLot.id} className="sterile-list-card" style={{ minHeight: 'auto', padding: '15px', border: '1px solid rgba(142, 68, 173, 0.2)', background: 'rgba(142, 68, 173, 0.05)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <div>
                                  <strong style={{ fontSize: '1.1em', color: '#fff' }}>{masterLot.item_name}</strong>
                                  <div style={{ fontSize: '0.85em', color: minAvailable > 0 ? '#2ecc71' : '#e74c3c', marginTop: '4px', fontWeight: '600' }}>
                                    (ทำได้: {minAvailable} ชุด)
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                <button
                                  disabled={minAvailable <= 0 || !isSterileInfoComplete}
                                  onClick={() => handleCreateUniqueCode(masterLot, null)}
                                  className="sterile-confirm-btn"
                                  style={{ flex: 1, padding: '10px', fontSize: '0.9em', width: 'auto', background: 'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)', boxShadow: 'none' }}
                                >
                                  ⚡ สร้างรหัสใหม่
                                </button>
                                <button
                                  disabled={minAvailable <= 0 || !isSterileInfoComplete}
                                  onClick={() => setScanBlankItem(masterLot)}
                                  className="sterile-confirm-btn"
                                  style={{ flex: 1, padding: '10px', fontSize: '0.9em', width: 'auto', background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)', boxShadow: 'none' }}
                                >
                                  📷 สแกนฉลากเปล่า
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 2. ส่วนของ "ชิ้นเดี่ยว" พร้อมระบบกรองข้อมูลตามกล่อง */}
                  {(() => {
                    let targetBoxReqs = {};
                    let targetBoxAssigned = {};
                    if (targetBoxForPack) {
                      const tBarcode = targetBoxForPack.split('|')[0];
                      const boxInst = instruments.find(i => i.unique_barcode === tBarcode);
                      if (boxInst) {
                        const boxTemplate = itemMaster.find(m => m.id === boxInst.master_id);
                        boxTemplate?.components?.forEach(compId => {
                          targetBoxReqs[compId] = (targetBoxReqs[compId] || 0) + 1;
                        });
                        instruments.forEach(i => {
                          if (i.is_active && (i.container_code === tBarcode || i.details?.targetBoxBarcode === tBarcode)) {
                            targetBoxAssigned[i.master_id] = (targetBoxAssigned[i.master_id] || 0) + 1;
                          }
                        });
                      }
                    }

                    return (
                      <div>
                        <h4 style={{ color: '#b3e5fc', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          🔧 จัดแบบชิ้นเดี่ยว {targetBoxForPack && <span style={{ color: '#ffb74d', fontSize: '0.85em' }}>(แสดงเฉพาะของที่กล่องขาด)</span>}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '15px' }}>
                          {itemMaster.filter(masterLot => {
                            if (targetBoxForPack) {
                              const reqQty = targetBoxReqs[masterLot.id] || 0;
                              const assignedQty = targetBoxAssigned[masterLot.id] || 0;
                              return (reqQty - assignedQty) > 0;
                            } else {
                              if (masterLot.is_set || (masterLot.category !== 'วัสดุคงทน' && masterLot.category !== 'หัวกรอ')) return false;
                              return true;
                            }
                          }).map(masterLot => {
                            let washingAvail = 0;
                            if (masterLot.is_set) {
                              washingAvail = masterLot.components.reduce((min, compId) => {
                                const ws = supplies.find(s => s.master_id === compId && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
                                return Math.min(min, ws?.item_amount || 0);
                              }, 9999);
                              if (washingAvail === 9999) washingAvail = 0;
                            } else {
                              const ws = supplies.find(s => s.master_id === masterLot.id && s.warehouse_name.includes("ห้องล้างเครื่องมือ"));
                              washingAvail = ws?.item_amount || 0;
                            }

                            let maxPackable = washingAvail;
                            let missingText = '';

                            if (targetBoxForPack) {
                              const reqQty = targetBoxReqs[masterLot.id] || 0;
                              const assignedQty = targetBoxAssigned[masterLot.id] || 0;
                              const missingQty = reqQty - assignedQty;
                              maxPackable = Math.min(washingAvail, missingQty);
                              missingText = `(กล่องขาด ${missingQty})`;
                            }

                            return (
                              <div key={masterLot.id} className="sterile-list-card" style={{ minHeight: 'auto', padding: '15px', border: '1px solid rgba(52, 152, 219, 0.2)', background: 'rgba(52, 152, 219, 0.03)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                  <div>
                                    <strong style={{ fontSize: '1.1em', color: '#fff' }}>
                                      {masterLot.is_set ? '📦 ' : ''}{masterLot.item_name}
                                    </strong>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                                      <span style={{ fontSize: '0.85em', color: washingAvail > 0 ? '#2ecc71' : '#e74c3c', fontWeight: '600' }}>
                                        (มีห้องล้าง: {washingAvail})
                                      </span>
                                      {missingText && (
                                        <span style={{ fontSize: '0.85em', color: '#ffb74d', fontWeight: 'bold' }}>
                                          {missingText}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                  <button
                                    disabled={maxPackable <= 0 || !isSterileInfoComplete}
                                    onClick={() => handleCreateUniqueCode(masterLot, null)}
                                    className="sterile-confirm-btn"
                                    style={{ flex: 1, padding: '10px', fontSize: '0.9em', width: 'auto', background: 'linear-gradient(135deg, #f39c12 0%, #d35400 100%)', boxShadow: 'none' }}
                                  >
                                    ⚡ สร้างใหม่
                                  </button>
                                  <button
                                    disabled={maxPackable <= 0 || !isSterileInfoComplete}
                                    onClick={() => setScanBlankItem(masterLot)}
                                    className="sterile-confirm-btn"
                                    style={{ flex: 1, padding: '10px', fontSize: '0.9em', width: 'auto', background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)', boxShadow: 'none' }}
                                  >
                                    📷 ฉลากเปล่า
                                  </button>
                                </div>
                              </div>
                            );
                          })}

                          {/* เช็คว่าแพ็กครบหรือยัง */}
                          {targetBoxForPack && itemMaster.filter(m => (targetBoxReqs[m.id] || 0) - (targetBoxAssigned[m.id] || 0) > 0).length === 0 && (
                            <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#2ecc71', padding: '25px', border: '2px dashed #2ecc71', borderRadius: '16px', fontWeight: 'bold', background: 'rgba(46, 204, 113, 0.05)', fontSize: '1.2em' }}>
                              🎉 กล่องนี้มีหัวกรอครบถ้วนตามแม่แบบแล้ว
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

            </div>

            {/* History logs card */}
            <div className="sterile-history-card" style={{ marginTop: '25px' }}>
              <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span>📋 ประวัติการแพ็กซอง</span>
                <input
                  type="date"
                  value={historyFilterDate}
                  onChange={(e) => setHistoryFilterDate(e.target.value)}
                  className="sterile-date-picker"
                  style={{ fontSize: '0.95em' }}
                />
              </h3>

              {selectedPrints.length > 0 && (
                <div style={{
                  marginBottom: '20px',
                  padding: '15px',
                  background: 'rgba(39, 174, 96, 0.1)',
                  border: '1px solid rgba(39, 174, 96, 0.3)',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <span style={{ fontWeight: 'bold', color: '#2ecc71', fontSize: '1.05em' }}>✅ คุณเลือกไว้ {selectedPrints.length} รายการ</span>
                  <button
                    onClick={handleBatchPrint}
                    disabled={isPrinting}
                    className="sterile-confirm-btn"
                    style={{ width: 'auto', padding: '10px 20px', fontSize: '0.95em', background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)', boxShadow: 'none' }}
                  >
                    {isPrinting ? '⏳ กำลังพิมพ์...' : '🖨️ พิมพ์รายการที่เลือกทั้งหมด'}
                  </button>
                </div>
              )}

              <div className="sterile-table-container">
                <table className="sterile-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', width: '50px' }}>
                        <input
                          type="checkbox"
                          style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const itemsToPrint = instruments.filter(i => i.details?.mfgDate === historyFilterDate && !i.details?.isPrinted);
                              setSelectedPrints(itemsToPrint);
                            } else {
                              setSelectedPrints([]);
                            }
                          }}
                          checked={
                            selectedPrints.length > 0 &&
                            selectedPrints.length === instruments.filter(i => i.details?.mfgDate === historyFilterDate && !i.details?.isPrinted).length
                          }
                        />
                      </th>
                      <th>รหัส (Barcode)</th>
                      <th style={{ textAlign: 'left' }}>ชื่อวัสดุ</th>
                      <th style={{ textAlign: 'center' }}>ผู้เตรียม/ตรวจ</th>
                      <th style={{ textAlign: 'center', color: '#ffb74d' }}>วันหมดอายุ</th>
                      <th style={{ textAlign: 'center' }}>จัดการ (รายชิ้น)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instruments.filter(i => i.details?.mfgDate === historyFilterDate).map(item => (
                      <tr key={item.id} style={{ background: selectedPrints.some(s => s.id === item.id) ? 'rgba(0, 229, 255, 0.08)' : 'transparent' }}>
                        <td style={{ textAlign: 'center' }}>
                          {!item.details?.isPrinted && (
                            <input
                              type="checkbox"
                              style={{ transform: 'scale(1.3)', cursor: 'pointer' }}
                              checked={selectedPrints.some(s => s.id === item.id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedPrints(prev => [...prev, item]);
                                else setSelectedPrints(prev => prev.filter(s => s.id !== item.id));
                              }}
                            />
                          )}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', fontFamily: 'monospace', fontSize: '1.1em' }}>{item.unique_barcode}</td>
                        <td>
                          <span style={{ fontWeight: '500' }}>{item.item_name}</span>
                          <br />
                          <small style={{ color: 'rgba(255,255,255,0.5)' }}>[{item.details?.packaging}]</small>
                        </td>
                        <td style={{ textAlign: 'center' }}>{item.details?.preparedBy} / {item.details?.checkedBy}</td>
                        <td style={{ textAlign: 'center', color: '#ffb74d', fontWeight: 'bold' }}>{item.details?.expDate}</td>
                        <td style={{ textAlign: 'center', minWidth: '150px' }}>
                          {item.details?.isPrinted ? (
                            <button className="sterile-confirm-btn" style={{ background: 'rgba(46, 204, 113, 0.15)', border: '1px solid rgba(46, 204, 113, 0.3)', color: '#2ecc71', cursor: 'default', padding: '6px 12px', fontSize: '0.85em', width: 'auto', boxShadow: 'none' }}>✅ ปริ้นแล้ว</button>
                          ) : (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button onClick={() => setPrintPreviewItem(item)} className="sterile-confirm-btn" style={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', padding: '6px 12px', fontSize: '0.85em', width: 'auto', boxShadow: 'none' }}>🔍 พรีวิว</button>
                              <button onClick={() => handleCancelPackaging(item)} className="sterile-confirm-btn" style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', padding: '6px 12px', fontSize: '0.85em', width: 'auto', boxShadow: 'none' }}>❌ ยกเลิก</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {instruments.filter(i => i.details?.mfgDate === historyFilterDate).length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.4)', fontSize: '1.05em' }}>
                          ไม่มีประวัติการแพ็กซองในวันนี้
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
  );
};

export default PackagingPage;
